import React, { useEffect } from "react";
import { Form, Input, Button, message } from "antd";
import { useMutation, gql } from "@apollo/client";

const { TextArea } = Input;

// GraphQL mutations
const CREATE_CONDITION_MUTATION = gql`
  mutation CreateCondition($name: String!, $description: String) {
    createCondition(name: $name, description: $description) {
      success
      message
      condition {
        name
        description
      }
    }
  }
`;

const UPDATE_CONDITION_MUTATION = gql`
  mutation UpdateCondition($id: ID!, $name: String, $description: String) {
    updateCondition(id: $id, name: $name, description: $description) {
      success
      message
      condition {
        id
        name
        description
      }
    }
  }
`;

// ✅ Renamed interface
interface HandleConditionsProps {
  onClose: () => void;
  editConditionData?: any;
  refreshData?: () => void;
}

// ✅ Renamed component
const HandleConditions: React.FC<HandleConditionsProps> = ({
  onClose,
  editConditionData,
  refreshData,
}) => {
  const [form] = Form.useForm();

  const [createCondition] = useMutation(CREATE_CONDITION_MUTATION);
  const [updateCondition] = useMutation(UPDATE_CONDITION_MUTATION);

  useEffect(() => {
    if (editConditionData) {
      form.setFieldsValue({
        name: editConditionData.name ?? "",
        description: editConditionData.description ?? "",
      });
    } else {
      form.resetFields();
    }
  }, [editConditionData, form]);

  const onFinish = async (values: any) => {
  try {
    if (editConditionData) {
      const conditionId = editConditionData.id || editConditionData._id;

      if (!conditionId) {
        message.error("Missing condition ID");
        return;
      }

      const { data } = await updateCondition({
        variables: {
          id: conditionId, // ✅ always defined now
          name: values.name,
          description: values.description || "",
        },
      });

      if (data?.updateCondition?.success) {
        message.success("Condition updated successfully");
        refreshData?.();
        onClose();
      } else {
        message.error(data?.updateCondition?.message || "Update failed");
      }
    } else {
      const { data } = await createCondition({
        variables: {
          name: values.name,
          description: values.description || "",
        },
      });

      if (data?.createCondition?.success) {
        message.success("Condition created successfully");
        refreshData?.();
        onClose();
      } else {
        message.error(data?.createCondition?.message || "Create failed");
      }
    }
  } catch (err: any) {
    console.error(err);
    message.error(err.message || "Something went wrong");
  }
};

  return (
    <div>
      <h2>{editConditionData ? "Edit Condition" : "Add Condition"}</h2>

      <button onClick={onClose}>Back to Conditions</button>

      <Form
        form={form}
        name="handleConditionsForm" // ✅ optional rename
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onFinish={onFinish}
        autoComplete="off"
      >
        <Form.Item
          label="Condition Name"
          name="name"
          rules={[
            { required: true, message: "Condition name is required" },
            {
              pattern: /^[a-zA-Z0-9 ]+$/,
              message: "Only alphanumeric characters allowed",
            },
            {
              max: 50,
              message: "Name cannot exceed 50 characters",
            },
          ]}
        >
          <Input placeholder="Enter condition name" maxLength={50} />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[
            {
              min: 10,
              message: "Description must be at least 10 characters",
            },
            {
              max: 200,
              message: "Description cannot exceed 200 characters",
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder="Enter description (optional)"
            maxLength={200}
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <Button type="primary" htmlType="submit">
            {editConditionData ? "Update Condition" : "Create Condition"}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

// ✅ Renamed export
export default HandleConditions;